import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewYoutube } from '../../../services/viewYoutubeService';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { X, Save } from 'lucide-react';

interface EditVideoDialogProps {
    isOpen: boolean;
    onClose: () => void;
    video: ViewYoutube | null;
    onUpdate: (id: number, data: Partial<ViewYoutube>) => Promise<boolean>;
}

export const EditVideoDialog: React.FC<EditVideoDialogProps> = ({ isOpen, onClose, video, onUpdate }) => {
    const { t } = useTranslation('common');
    const [formData, setFormData] = useState<Partial<ViewYoutube>>({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (video) {
            setFormData({
                Content: video.Content || '',
                Country: video.Country || 'Vietnam',
                View: video.View || 0,
                NameChannel: video.NameChannel || '',
                Uploader: video.Uploader || '',
                Linkvideo: video.Linkvideo || '',
                status: video.status || 'active',
            });
        }
    }, [video]);

    if (!isOpen || !video) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!video.id) return;
        setSaving(true);
        const success = await onUpdate(video.id, formData);
        setSaving(false);
        if (success) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold text-gray-900">{t('checkView.modal.editTitle')}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-500 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t('checkView.modal.link')}</label>
                        <Input
                            value={formData.Linkvideo}
                            onChange={e => setFormData({ ...formData, Linkvideo: e.target.value })}
                            required
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">{t('checkView.modal.content')}</label>
                        <Input
                            value={formData.Content}
                            onChange={e => setFormData({ ...formData, Content: e.target.value })}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">{t('checkView.modal.channel')}</label>
                            <Input
                                value={formData.NameChannel}
                                onChange={e => setFormData({ ...formData, NameChannel: e.target.value })}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">{t('checkView.modal.country')}</label>
                            <Input
                                value={formData.Country}
                                onChange={e => setFormData({ ...formData, Country: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">{t('checkView.modal.views')}</label>
                            <Input
                                type="number"
                                value={formData.View}
                                onChange={e => setFormData({ ...formData, View: Number(e.target.value) })}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">{t('checkView.modal.status')}</label>
                            <select
                                value={formData.status}
                                onChange={e => setFormData({ ...formData, status: e.target.value })}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end gap-3">
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
                            {t('checkView.actions.cancel')}
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Saving...' : (
                                <>
                                    <Save className="w-4 h-4 mr-2" />
                                    {t('checkView.actions.update')}
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
